import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  X,
  ChevronDown,
  Circle,
  CircleDashed,
  CheckCircle,
  ExternalLink,
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { MarkdownPreview } from '@/shared/components'

// Section status types
export type SectionStatus = 'empty' | 'draft' | 'complete'

// Section completion status
export interface SpecSection {
  key: string
  status: SectionStatus
  content: string
  preview: string // First line truncated
  wordCount: number
}

interface SpecsPreviewModalProps {
  isOpen: boolean
  onClose: () => void
  sections: SpecSection[]
  onGeneratePdf: () => void
  isGenerating?: boolean
  shortId?: string
}

// The 9 spec sections keys (matching scoping.sections in i18n)
// Note: These use camelCase to match i18n keys (successCriteria)
// The Edge Function NicolAI uses snake_case (success_criteria) which is
// mapped in handleSpecsUpdate in scoping-v2-page.tsx
export const SPEC_SECTION_KEYS = [
  'summary',
  'context',
  'users',
  'features',
  'workflows',
  'architecture',
  'constraints',
  'risks',
  'successCriteria',
] as const

// Status indicator component
const STATUS_CONFIG = {
  empty: {
    icon: Circle,
    iconClass: 'text-foreground-muted',
    bgClass: 'bg-background-secondary',
  },
  draft: {
    icon: CircleDashed,
    iconClass: 'text-amber-600',
    bgClass: 'bg-amber-50',
  },
  complete: {
    icon: CheckCircle,
    iconClass: 'text-success',
    bgClass: 'bg-success/10',
  },
}

function StatusDot({ status }: { status: SectionStatus }) {
  const { icon: Icon, iconClass, bgClass } = STATUS_CONFIG[status]

  return (
    <div
      className={cn(
        'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
        bgClass
      )}
    >
      <Icon className={cn('h-4 w-4', iconClass)} />
    </div>
  )
}

// Accordion item component
function SectionAccordionItem({
  section,
  isOpen,
  onToggle,
}: {
  section: SpecSection
  isOpen: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()

  return (
    <div
      className={cn(
        'overflow-hidden rounded-lg border transition-colors',
        section.status === 'complete' && 'border-success/30',
        section.status === 'draft' && 'border-amber-200',
        section.status === 'empty' && 'border-border'
      )}
    >
      {/* Header cliquable */}
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between p-4 text-left transition-colors hover:bg-background-secondary/50"
      >
        <div className="flex min-w-0 items-center gap-3">
          <StatusDot status={section.status} />
          <div className="min-w-0">
            <span className="text-sm font-medium text-foreground">
              {t(`scoping.sections.${section.key}`)}
            </span>
            {/* Preview 1 ligne quand fermé */}
            {!isOpen && section.preview && (
              <p className="mt-0.5 truncate text-xs text-foreground-muted">
                {section.preview}
              </p>
            )}
          </div>
        </div>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-foreground-muted transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {/* Contenu expandable */}
      {isOpen && section.content && (
        <div className="border-t border-border/50 p-4">
          <MarkdownPreview content={section.content} />
        </div>
      )}

      {/* Message si vide */}
      {isOpen && !section.content && (
        <div className="border-t border-border/50 p-4">
          <p className="text-sm italic text-foreground-muted">
            {t('scopingV2.preview.emptySection')}
          </p>
        </div>
      )}
    </div>
  )
}

/**
 * Specs Preview Modal - Slide-in panel from right
 * Shows 9 sections with accordions and 3-state indicators
 * Design reference: SCOP2-005, SCOP2-008b
 */
export function SpecsPreviewModal({
  isOpen,
  onClose,
  sections,
  onGeneratePdf,
  isGenerating = false,
  shortId,
}: SpecsPreviewModalProps) {
  const { t } = useTranslation()

  // Track which sections are open (multi-open allowed)
  const [openSections, setOpenSections] = useState<Set<string>>(new Set())

  const toggleSection = (key: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  // Calculate completion counts (for display only, not blocking)
  const completeCount = sections.filter((s) => s.status === 'complete').length
  const totalCount = sections.length

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Slide-in panel */}
      <div
        className={cn(
          'fixed right-0 top-0 z-50 flex h-full w-full flex-col bg-background shadow-xl sm:max-w-lg',
          'transform transition-transform duration-300 ease-out',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Header */}
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-border px-6">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {t('scopingV2.preview.title')}
            </h2>
            <p className="text-sm text-foreground-muted">
              {t('scopingV2.preview.sectionsComplete', {
                complete: completeCount,
                total: totalCount,
              })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Lien preview (nouvel onglet) */}
            {shortId && (
              <a
                href={`/scoping/${shortId}/preview`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-9 w-9 items-center justify-center rounded-lg text-foreground-muted transition-colors hover:bg-background-secondary hover:text-foreground"
                title={t('scopingV2.preview.openInNewTab')}
              >
                <ExternalLink className="h-5 w-5" />
              </a>
            )}
            {/* Close button */}
            <button
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-foreground-muted transition-colors hover:bg-background-secondary hover:text-foreground"
              aria-label={t('common.cancel')}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Sections list (scrollable) */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-3">
            {SPEC_SECTION_KEYS.map((key) => {
              const section = sections.find((s) => s.key === key)
              if (!section) return null

              return (
                <SectionAccordionItem
                  key={key}
                  section={section}
                  isOpen={openSections.has(key)}
                  onToggle={() => toggleSection(key)}
                />
              )
            })}
          </div>
        </div>

        {/* Footer with Generate PDF button */}
        <div className="shrink-0 border-t border-border p-6">
          <button
            onClick={onGeneratePdf}
            disabled={isGenerating}
            className={cn(
              'flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold transition-colors',
              !isGenerating
                ? 'bg-accent text-white hover:bg-accent-hover'
                : 'cursor-not-allowed bg-background-secondary text-foreground-muted'
            )}
          >
            {isGenerating
              ? t('scopingV2.preview.generating')
              : t('scopingV2.preview.generatePdf')}
          </button>
        </div>
      </div>
    </>
  )
}
